import { bytesToHex, type Hex } from '@/store/ids';
import type { JsonValue } from '@/store/types';

/**
 * RFC 8785 JSON Canonicalization Scheme, restricted to the value space this
 * client actually hashes.
 *
 * Section 11 requires RFC 8785 "through a verified conforming implementation".
 * The frozen dependency set contains no such package and Section 11 also
 * forbids adding one, so the scheme is implemented here over a deliberately
 * narrowed input domain and the narrowing is enforced at run time rather than
 * assumed:
 *
 *   - Numbers must be safe integers. RFC 8785's number rule is the hard part of
 *     the specification — it is ECMAScript `Number::toString` with a specific
 *     shortest-round-trip algorithm — and every field this client normalizes is
 *     an identifier, an offset, a length or a sequence. Rejecting anything else
 *     means the only numbers ever emitted are integers, where `String(n)` and
 *     the RFC 8785 rule provably agree.
 *   - Strings are serialized with `JSON.stringify`, which since the
 *     well-formed-stringify change escapes lone surrogates as `\uXXXX` and
 *     otherwise emits exactly the escapes RFC 8785 requires.
 *   - Object keys are sorted by UTF-16 code unit, which is what the default
 *     `Array.prototype.sort` comparison does for strings, and what RFC 8785
 *     specifies.
 *
 * Anything outside that domain throws instead of being silently canonicalized
 * by a non-conforming rule, because a hash that quietly disagrees with the
 * server's is worse than a failed send: it would make a retried operation look
 * like a different intent and be rejected as an operation conflict.
 */
export function canonicalize(value: JsonValue): string {
  if (value === null) {
    return 'null';
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (typeof value === 'number') {
    if (!Number.isSafeInteger(value)) {
      throw new Error(
        'canonical JSON in this client is restricted to safe integers; ' +
          `received ${String(value)}`,
      );
    }
    // Negative zero has to normalize to "0"; RFC 8785 has no signed zero.
    return String(value === 0 ? 0 : value);
  }
  if (typeof value === 'string') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => canonicalize(item)).join(',')}]`;
  }

  const entries = Object.entries(value as { readonly [key: string]: JsonValue });
  // Sorting is over UTF-16 code units, which is the default string comparison.
  entries.sort((left, right) => (left[0] < right[0] ? -1 : left[0] > right[0] ? 1 : 0));
  const body = entries
    .map(([key, item]) => `${JSON.stringify(key)}:${canonicalize(item)}`)
    .join(',');
  return `{${body}}`;
}

/**
 * The 32-byte payload hash Section 13 stores beside an operation result.
 *
 * `crypto.subtle` requires a secure context. That is a real constraint rather
 * than an inconvenience here: the whole application requires HTTPS for the
 * identity origin and WSS for the transport, so a context where this is missing
 * cannot send anything anyway, and failing loudly is correct.
 */
export async function sha256Hex(input: string): Promise<Hex> {
  if (!crypto.subtle) {
    throw new Error(
      'Web Crypto is unavailable. The client requires a secure context (HTTPS or localhost).',
    );
  }
  const encoded = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', encoded);
  return bytesToHex(new Uint8Array(digest));
}

export async function payloadHash(payload: JsonValue): Promise<Hex> {
  return sha256Hex(canonicalize(payload));
}
